import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Download, Plus, Search, Upload, Users } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { roleOptions as staticRoleOptions } from '../portal/portalConfig';
import { RoundedSelect } from '../components/RoundedSelect';
import {
  TableColumnFilter,
  buildColumnFilterOptions,
  normalizeColumnFilterValue,
} from '../components/TableColumnFilter';
import { FloatingNotices } from '../components/FloatingNotices';
import { fetchJsonCached, getCachedJson } from '../utils/portalQueryCache';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import { usersRowAnimate, usersRowInitial, usersRowTransition } from '../utils/motionReveal';

const PAGE_SIZE = 20;
const USERS_FETCH_SIZE = 200;

function getUserDisplayName(entry) {
  return [entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'No name';
}

function resolveCustomerMasterLoginFromAccount(customerAccountId, accounts) {
  if (!customerAccountId) {
    return '';
  }

  const selected = (accounts || []).find((item) => String(item.id) === String(customerAccountId));
  return selected ? String(selected.name || '').trim() : '';
}

function getUserAvatarUrl(entry) {
  return entry?.avatarThumbUrl || entry?.avatarUrl || '';
}

function UserAvatar({ entry }) {
  const [error, setError] = useState(false);
  const thumbUrl = String(entry?.avatarThumbUrl || '').trim();
  const fullUrl = String(entry?.avatarUrl || '').trim();
  const url = String((error && fullUrl && thumbUrl && fullUrl !== thumbUrl ? fullUrl : getUserAvatarUrl(entry)) || '').trim();
  const label = getUserDisplayName(entry);
  const initial = (label || entry?.email || 'U').slice(0, 1).toUpperCase();

  if (!url || error) {
    return (
      <div className="user-list-avatar-fallback" aria-hidden="true">
        {initial}
      </div>
    );
  }

  return (
    <img
      className="user-list-avatar-image"
      src={url}
      alt=""
      loading="lazy"
      onError={() => setError(true)}
    />
  );
}

function getUserStatusLabel(entry) {
  return entry.statusLabel || (entry.isActive ? 'Active' : 'Inactive');
}

function normalizeGroups(entry) {
  const groups = Array.isArray(entry?.groups) ? entry.groups : [];
  return groups
    .map((g) => ({
      id: g?.id ? String(g.id) : '',
      name: String(g?.name || '').trim(),
    }))
    .filter((g) => g.id || g.name);
}

function normalizeGroupScopes(entry) {
  const scopes = Array.isArray(entry?.groupScopes) ? entry.groupScopes : [];
  return scopes
    .map((s) => ({
      groupId: s?.groupId != null ? Number(s.groupId) : null,
      groupName: String(s?.groupName || '').trim(),
      customerAccountId: s?.customerAccountId != null ? Number(s.customerAccountId) : null,
      customerAccountName: String(s?.customerAccountName || '').trim(),
      storeId: s?.storeId != null ? Number(s.storeId) : null,
      storeName: String(s?.storeName || '').trim(),
      roleCode: String(s?.roleCode || '').trim().toUpperCase(),
      roleLabel: String(s?.roleLabel || '').trim(),
    }))
    .filter((s) => Number.isFinite(s.groupId) && s.groupId > 0);
}

function filterGroupScopesForActor(scopes, { isHkAdmin, groupAdminGroupIds, storeAdminStoreIds }) {
  if (!scopes?.length || isHkAdmin) return scopes;
  const groupIds = Array.isArray(groupAdminGroupIds) ? groupAdminGroupIds : [];
  const storeIds = Array.isArray(storeAdminStoreIds) ? storeAdminStoreIds : [];
  if (groupIds.length === 0 && storeIds.length === 0) return [];
  return scopes.filter(
    (s) => groupIds.includes(s.groupId) || (s.storeId != null && storeIds.includes(Number(s.storeId)))
  );
}

function filterGroupsForActor(groups, { isHkAdmin, groupAdminGroupIds }) {
  if (!groups?.length || isHkAdmin) return groups;
  const groupIds = Array.isArray(groupAdminGroupIds) ? groupAdminGroupIds : [];
  if (groupIds.length === 0) return [];
  return groups.filter((g) => groupIds.includes(Number(g.id)));
}

function isGroupAdminEntry(entry) {
  return String(entry?.roleCode || '').toUpperCase() === 'GROUP_ADMIN';
}

function isSupportEntry(entry) {
  return String(entry?.roleCode || '').toUpperCase() === 'SUPPORT';
}

function isGroupWideRoleEntry(entry) {
  return isGroupAdminEntry(entry) || isSupportEntry(entry);
}

function isCustomerMasterRoleEntry(entry) {
  return String(entry?.roleCode || '').toUpperCase() === 'CUSTOMER_MASTER';
}

function countUserGroupScopes(entry) {
  const scopes = normalizeGroupScopes(entry);
  if (scopes.length > 0) return scopes.length;
  if (isGroupAdminEntry(entry)) {
    const groups = normalizeGroups(entry);
    return groups.length > 0 ? groups.length : entry?.groupId ? 1 : 0;
  }
  return entry?.groupId ? 1 : 0;
}

function getPrimaryRoleCode(entry) {
  return String(entry?.__primaryRoleCode || entry?.roleCode || '').toUpperCase();
}

function hasGlobalPrimaryRole(entry) {
  return getPrimaryRoleCode(entry) === 'HK_SUPPORT';
}

function willRemoveScopeOnly(entry) {
  // Never treat the highest/global role row as a scope removal.
  if (entry?.__isPrimaryRow) return false;
  if (entry?.groupId == null && entry?.storeId == null) return false;
  const scopes = normalizeGroupScopes(entry);
  // Keep HK Administrator (global primary) when removing a store/group row.
  if (hasGlobalPrimaryRole(entry) && (scopes.length >= 1 || entry?.groupId != null)) return true;
  return countUserGroupScopes(entry) > 1;
}

function getDeleteConfirmMessage(entry) {
  if (willRemoveScopeOnly(entry)) {
    const groupLabel = entry.groupName || `group ${entry.groupId}`;
    return `Remove this user's access in ${groupLabel}? The primary role record will remain.`;
  }
  return 'This will delete the user in Kinde (if linked) and remove all portal access. Continue?';
}

function formatUserGroupLabel(entry) {
  return entry?.groupName || '';
}

function formatUserStoreLabel(entry) {
  if (isGroupWideRoleEntry(entry) || isCustomerMasterRoleEntry(entry)) return 'All';
  return entry?.storeName || '';
}

function formatUserCustomerLabel(entry) {
  if (isGroupWideRoleEntry(entry)) return 'All';
  return entry?.customerAccountName || '';
}

function roleCodeToLabel(roleCode) {
  const code = String(roleCode || '').toUpperCase();
  if (code === 'CUSTOMER_MASTER') return 'Customer Master';
  if (code === 'GROUP_ADMIN') return 'Group-Admin';
  if (code === 'SUPPORT') return 'HK Support';
  if (code === 'STORE_ADMIN') return 'Store-Admin';
  if (code === 'HK_SUPPORT') return 'HK Administrator';
  if (code === 'SALES_STAFF') return 'Sales Staff';
  if (code === 'MASTER_SALES_STAFF') return 'Master Sales Staff';
  return String(roleCode || '').trim();
}

const INTERNAL_ROLE_CODES = ['HK_SUPPORT', 'SUPPORT'];

function buildGroupedRoleSelectOptions(roles) {
  const list = Array.isArray(roles) ? roles : [];
  const byCode = new Map(
    list.map((role) => [String(role.code || role.value || '').toUpperCase(), role])
  );

  const options = [];
  const internal = INTERNAL_ROLE_CODES
    .map((code) => byCode.get(code))
    .filter(Boolean)
    .map((role) => ({ value: role.code || role.value, label: role.label }));

  if (internal.length > 0) {
    options.push({ type: 'header', label: 'Internal' });
    options.push(...internal);
  }

  const external = list
    .filter((role) => !INTERNAL_ROLE_CODES.includes(String(role.code || role.value || '').toUpperCase()))
    .map((role) => ({ value: role.code || role.value, label: role.label }));

  if (external.length > 0) {
    options.push({ type: 'header', label: 'External' });
    options.push(...external);
  }

  return options;
}

function resolveScopeRole(scope, entry) {
  const entryRoleCode = String(entry?.roleCode || '').toUpperCase();
  const scopeRoleCode = String(scope?.roleCode || '').trim().toUpperCase()
    || (entryRoleCode === 'CUSTOMER_MASTER' ? 'CUSTOMER_MASTER' : '');
  if (scopeRoleCode) {
    return {
      roleCode: scopeRoleCode,
      roleLabel: scope?.roleLabel || roleCodeToLabel(scopeRoleCode),
    };
  }
  if (entryRoleCode === 'CUSTOMER_MASTER') {
    return { roleCode: 'CUSTOMER_MASTER', roleLabel: 'Customer Master' };
  }
  return {
    roleCode: entry.roleCode || 'SALES_STAFF',
    roleLabel: entry.roleLabel || roleCodeToLabel(entry.roleCode || 'SALES_STAFF'),
  };
}

function passesColumnFilters(entry, columnFilters, optionsByKey) {
  for (const [key, selected] of Object.entries(columnFilters)) {
    if (!selected) continue;
    const options = optionsByKey[key];
    if (!options || options.length === 0) continue;
    if (selected.size === 0) return false;
    if (selected.size >= options.length) continue;
    const getter = COLUMN_FILTER_GETTERS[key];
    if (!getter) continue;
    const value = normalizeColumnFilterValue(getter(entry));
    if (!selected.has(value)) return false;
  }
  return true;
}

const COLUMN_FILTER_GETTERS = {
  user: getUserDisplayName,
  email: (entry) => entry.email,
  status: getUserStatusLabel,
  role: (entry) => entry.roleLabel || entry.roleCode,
  customer: (entry) => formatUserCustomerLabel(entry),
  group: formatUserGroupLabel,
  store: formatUserStoreLabel,
};

export function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState(() => {
    const cached = getCachedJson('/api/admin/roles/users');
    if (Array.isArray(cached)) return cached;
    if (cached && Array.isArray(cached.items)) return cached.items;
    return [];
  });
  const [roleOptions, setRoleOptions] = useState(() => getCachedJson('/api/admin/roles/options') || staticRoleOptions);
  const [groups, setGroups] = useState(() => getCachedJson('/api/admin/roles/groups') || []);
  const [stores, setStores] = useState(() => getCachedJson('/api/admin/roles/stores') || []);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [roleFilter, setRoleFilter] = useState('All');
  const [sort, setSort] = useState({ key: '', dir: null });
  const [columnFilters, setColumnFilters] = useState({});
  const [usersRemoteTotal, setUsersRemoteTotal] = useState(() => {
    const cached = getCachedJson('/api/admin/roles/users');
    if (cached && typeof cached.total === 'number') return cached.total;
    if (Array.isArray(cached)) return cached.length;
    if (cached && Array.isArray(cached.items)) return cached.items.length;
    return 0;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(() => (getCachedJson('/api/admin/roles/users') ? false : true));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createAttempted, setCreateAttempted] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [openMenuFor, setOpenMenuFor] = useState(0);
  const [rowMenuPos, setRowMenuPos] = useState(null);
  const [rowMenuAnchorRect, setRowMenuAnchorRect] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [isActionBusy, setIsActionBusy] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const menuRootRef = useRef(null);
  const rowMenuPortalRef = useRef(null);
  const [scopeModal, setScopeModal] = useState(null);
  const [scopeDraft, setScopeDraft] = useState({ groupId: '', storeId: '' });
  const [scopeStores, setScopeStores] = useState(null);
  const [storesModal, setStoresModal] = useState(null);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [manageStores, setManageStores] = useState([]);
  const [storesModalSearch, setStoresModalSearch] = useState('');
  const [createDraft, setCreateDraft] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    roleCode: 'SALES_STAFF',
    groupId: '',
    storeId: '',
    password: '',
    confirmPassword: '',
  });
  const [createSupportGroupIds, setCreateSupportGroupIds] = useState([]);
  const [createSupportGroupsPickerOpen, setCreateSupportGroupsPickerOpen] = useState(false);
  const [createSupportGroupsPickerQuery, setCreateSupportGroupsPickerQuery] = useState('');
  const createSupportGroupsPickerRef = useRef(null);
  const [supportGroupsModal, setSupportGroupsModal] = useState(null);
  const [supportGroupsDraft, setSupportGroupsDraft] = useState([]);
  const [supportGroupsPickerOpen, setSupportGroupsPickerOpen] = useState(false);
  const [supportGroupsPickerQuery, setSupportGroupsPickerQuery] = useState('');
  const supportGroupsPickerRef = useRef(null);
  const [createGroupStores, setCreateGroupStores] = useState([]);
  const [generalDraft, setGeneralDraft] = useState({
    customerAccountId: '',
    login: '',
    password: '',
    email: '',
  });
  const [generalCustomerAccounts, setGeneralCustomerAccounts] = useState([]);
  const [generalPasswordModal, setGeneralPasswordModal] = useState(null);
  const [generalPasswordDraft, setGeneralPasswordDraft] = useState({ password: '', confirmPassword: '' });
  const [roleModal, setRoleModal] = useState(null);
  const [roleDraft, setRoleDraft] = useState({ roleCode: '', groupId: '', storeId: '' });
  const [roleModalStores, setRoleModalStores] = useState([]);
  const [isRoleSaving, setIsRoleSaving] = useState(false);

  const isHkAdmin = useMemo(() => String(user?.roleCode || '').toUpperCase() === 'HK_SUPPORT', [user?.roleCode]);
  const isSalesStaff = useMemo(() => user?.role === 'Sales Staff', [user?.role]);
  const isSupport = useMemo(() => String(user?.roleCode || '').toUpperCase() === 'SUPPORT', [user?.roleCode]);
  const isCustomerMaster = useMemo(() => user?.role === 'Customer Master', [user?.role]);
  const isGroupAdmin = useMemo(
    () => user?.role === 'Group-Admin' || (Array.isArray(user?.groupAdminGroupIds) && user.groupAdminGroupIds.length > 0),
    [user?.role, user?.groupAdminGroupIds]
  );
  const isGroupScopeAdmin = isGroupAdmin || isCustomerMaster;
  const isStoreAdmin = useMemo(
    () => !isSalesStaff && !isSupport && (user?.role === 'Store-Admin' || (Array.isArray(user?.storeAdminStoreIds) && user.storeAdminStoreIds.length > 0)),
    [user?.role, user?.storeAdminStoreIds, isSalesStaff, isSupport]
  );
  const isMasterSalesStaff = useMemo(
    () => !isSalesStaff && !isSupport && (user?.role === 'Master Sales Staff' || (Array.isArray(user?.masterSalesStaffStoreIds) && user.masterSalesStaffStoreIds.length > 0)),
    [user?.role, user?.masterSalesStaffStoreIds, isSalesStaff, isSupport]
  );
  const isPureStoreAdmin = isStoreAdmin && !isGroupScopeAdmin && !isHkAdmin && !isMasterSalesStaff;
  const isPureMasterSalesStaff = isMasterSalesStaff && !isStoreAdmin && !isGroupScopeAdmin && !isHkAdmin;
  const canSee = useMemo(
    () => !isSalesStaff && !isSupport && (isHkAdmin || isGroupScopeAdmin || isStoreAdmin || isMasterSalesStaff),
    [isSalesStaff, isSupport, isHkAdmin, isGroupScopeAdmin, isStoreAdmin, isMasterSalesStaff]
  );
  const canManageCustomerMasters = useMemo(() => isHkAdmin || isGroupAdmin, [isHkAdmin, isGroupAdmin]);
  const currentUserEmail = useMemo(() => String(user?.email || '').trim().toLowerCase(), [user?.email]);
  const currentActorEntry = useMemo(
    () => users.find((u) => String(u?.email || '').trim().toLowerCase() === currentUserEmail) || null,
    [users, currentUserEmail]
  );
  const actorGroupIdStr = useMemo(() => {
    if (user?.groupId != null) return String(user.groupId);
    return currentActorEntry?.groupId ? String(currentActorEntry.groupId) : '';
  }, [user?.groupId, currentActorEntry?.groupId]);
  const actorStoreIdStr = useMemo(() => {
    if (user?.storeId != null) return String(user.storeId);
    return currentActorEntry?.storeId ? String(currentActorEntry.storeId) : '';
  }, [user?.storeId, currentActorEntry?.storeId]);
  const actorGroupLabel = useMemo(() => {
    const name = String(user?.groupName || currentActorEntry?.groupName || '').trim();
    if (name) return name;
    return actorGroupIdStr ? `Group ${actorGroupIdStr}` : 'Group';
  }, [user?.groupName, currentActorEntry?.groupName, actorGroupIdStr]);
  const actorStoreLabel = useMemo(() => {
    const name = String(user?.storeName || currentActorEntry?.storeName || '').trim();
    if (name) return name;
    return actorStoreIdStr ? `Store ${actorStoreIdStr}` : 'Store';
  }, [user?.storeName, currentActorEntry?.storeName, actorStoreIdStr]);
  const debouncedSearch = useDebouncedValue(search, 250);
  const usersQueryBase =
    `/api/admin/roles/users?search=${encodeURIComponent(debouncedSearch)}` +
    `&status=${encodeURIComponent(statusFilter === 'All' ? '' : statusFilter)}` +
    `&roleCode=${encodeURIComponent(roleFilter === 'All' ? '' : roleFilter)}`;
  const applyUsersPayload = (payload) => {
    if (!payload) return;
    if (Array.isArray(payload)) {
      setUsers(payload);
      setUsersRemoteTotal(payload.length);
      return;
    }
    if (Array.isArray(payload.items)) {
      setUsers(payload.items);
      const remoteTotal = typeof payload.total === 'number' ? payload.total : payload.items.length;
      setUsersRemoteTotal(remoteTotal);
    }
  };

  const fetchAllUsersPayload = useCallback(async (signal, { onFirstPage } = {}) => {
    const firstPageUrl = `${usersQueryBase}&page=1&pageSize=${encodeURIComponent(String(USERS_FETCH_SIZE))}`;
    const firstPage = await fetchJsonCached(firstPageUrl, { ttlMs: 15_000, signal });
    if (!firstPage || Array.isArray(firstPage)) {
      onFirstPage?.(firstPage);
      return firstPage;
    }

    const firstItems = Array.isArray(firstPage.items) ? firstPage.items : [];
    const total = typeof firstPage.total === 'number' ? firstPage.total : firstItems.length;
    const firstPayload = {
      ...firstPage,
      items: firstItems,
      total,
    };
    onFirstPage?.(firstPayload);

    if (total <= firstItems.length) {
      return firstPayload;
    }

    const totalPages = Math.max(1, Math.ceil(total / USERS_FETCH_SIZE));
    const remainingPages = Array.from({ length: totalPages - 1 }, (_, index) => index + 2);
    const rest = await Promise.all(
      remainingPages.map((page) =>
        fetchJsonCached(`${usersQueryBase}&page=${page}&pageSize=${encodeURIComponent(String(USERS_FETCH_SIZE))}`, {
          ttlMs: 15_000,
          signal,
        })
      )
    );

    const items = [
      ...firstItems,
      ...rest.flatMap((payload) => (Array.isArray(payload?.items) ? payload.items : [])),
    ];

    return {
      ...firstPage,
      items,
      total,
      page: 1,
      pageSize: items.length,
    };
  }, [usersQueryBase]);

  const onColumnFilterChange = (key, selected) => {
    setColumnFilters((current) => {
      const next = { ...current };
      if (!selected) {
        delete next[key];
      } else {
        next[key] = selected;
      }
      return next;
    });
    setCurrentPage(1);
  };

  const downloadExcelTemplate = async () => {
    try {
      const response = await fetch('/api/admin/roles/users/import-template', { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to download template.');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hk-identity-users-import-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Failed to download template.');
    }
  };

  useEffect(() => {
    const closeRowMenu = () => {
      setOpenMenuFor(0);
      setRowMenuPos(null);
      setRowMenuAnchorRect(null);
    };

    const onDocPointerDown = (event) => {
      // Menu is portaled to document.body; ignore clicks inside it or on any ⋯ trigger
      // (trigger onClick handles open/toggle). Clicks on empty panel space must close.
      if (!rowMenuPortalRef.current) return;
      if (rowMenuPortalRef.current.contains(event.target)) return;
      if (event.target.closest?.('.ellipsis-button')) return;
      closeRowMenu();
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeRowMenu();
        setConfirm(null);
      }
    };
    const onAnotherDropdownOpened = () => {
      closeRowMenu();
    };

    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('rounded-select:open', onAnotherDropdownOpened);
    document.addEventListener('table-column-filter:open', onAnotherDropdownOpened);
    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('rounded-select:open', onAnotherDropdownOpened);
      document.removeEventListener('table-column-filter:open', onAnotherDropdownOpened);
    };
  }, []);

  useEffect(() => {
    if (!openMenuFor) return undefined;
    const closeMenu = () => {
      setOpenMenuFor(0);
      setRowMenuPos(null);
      setRowMenuAnchorRect(null);
    };
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [openMenuFor]);

  useEffect(() => {
    if (!createOpen) return;
    setImportOpen(false);
    setOpenMenuFor(0);
    setRowMenuPos(null);
    setConfirm(null);
    setScopeModal(null);
    setStoresModal(null);
  }, [createOpen]);

  useEffect(() => {
    if (!importOpen) return;
    setCreateOpen(false);
    setOpenMenuFor(0);
    setRowMenuPos(null);
    setConfirm(null);
    setScopeModal(null);
    setStoresModal(null);
  }, [importOpen]);

  useEffect(() => {
    if (!openMenuFor) return;
    setCreateOpen(false);
    setImportOpen(false);
    setConfirm(null);
    setScopeModal(null);
    setStoresModal(null);
  }, [openMenuFor]);

  useEffect(() => {
    if (!confirm) return;
    setCreateOpen(false);
    setImportOpen(false);
    setOpenMenuFor(0);
    setRowMenuPos(null);
    setScopeModal(null);
    setStoresModal(null);
  }, [confirm]);

  useEffect(() => {
    if (!scopeModal) return;
    setCreateOpen(false);
    setImportOpen(false);
    setOpenMenuFor(0);
    setRowMenuPos(null);
    setConfirm(null);
    setStoresModal(null);
  }, [scopeModal]);

  useEffect(() => {
    if (!storesModal) return;
    setCreateOpen(false);
    setImportOpen(false);
    setOpenMenuFor(0);
    setRowMenuPos(null);
    setConfirm(null);
    setScopeModal(null);
  }, [storesModal]);

  useEffect(() => {
    if (storesModal) return;
    setStoresModalSearch('');
    setManageStores([]);
  }, [storesModal]);

  useEffect(() => {
    if (!error) return undefined;
    const handle = window.setTimeout(() => setError(''), 3000);
    return () => window.clearTimeout(handle);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const handle = window.setTimeout(() => setSuccess(''), 3000);
    return () => window.clearTimeout(handle);
  }, [success]);

  useEffect(() => {
    if (!canSee) {
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    const controller = new AbortController();
    const load = async () => {
      setError('');
      try {
        // Show users ASAP; lookups load in parallel and do not block the first paint.
        const groupsPromise = (isPureStoreAdmin || isPureMasterSalesStaff)
          ? Promise.resolve([])
          : fetchJsonCached('/api/admin/roles/groups', { ttlMs: 300_000, signal: controller.signal });
        const rolesPromise = fetchJsonCached('/api/admin/roles/options', { ttlMs: 300_000, signal: controller.signal });
        const storesPromise = fetchJsonCached('/api/admin/roles/stores', { ttlMs: 300_000, signal: controller.signal });

        const usersData = await fetchAllUsersPayload(controller.signal, {
          onFirstPage: (payload) => {
            if (isCancelled) return;
            applyUsersPayload(payload);
            setIsLoading(false);
          },
        });
        if (!isCancelled) {
          applyUsersPayload(usersData);
          setIsLoading(false);
        }

        const [rolesData, groupsData, storesData] = await Promise.all([rolesPromise, groupsPromise, storesPromise]);
        if (!isCancelled) {
          setRoleOptions(rolesData.length ? rolesData : staticRoleOptions);
          setGroups(groupsData || []);
          setStores(storesData || []);
        }
      } catch (loadError) {
        if (!isCancelled) {
          setError(loadError.message || 'Failed to load users.');
          setIsLoading(false);
        }
      }
    };

    load();
    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [canSee, fetchAllUsersPayload, isPureStoreAdmin, isPureMasterSalesStaff]);

  useEffect(() => {
    if (!canSee) return undefined;
    const poll = async () => {
      if (document.hidden) return;
      try {
        const updated = await fetchAllUsersPayload();
        applyUsersPayload(updated);
      } catch {
        /* keep list on transient errors */
      }
    };
    const id = window.setInterval(poll, 30_000);
    const onVis = () => {
      if (!document.hidden) void poll();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [canSee, fetchAllUsersPayload]);

  useEffect(() => {
    if (!canSee) return undefined;
    const refreshLookups = async () => {
      if (document.hidden) return;
      try {
        const groupsPromise = (isPureStoreAdmin || isPureMasterSalesStaff)
          ? Promise.resolve([])
          : fetchJsonCached('/api/admin/roles/groups', { ttlMs: 300_000 });
        const [rolesData, groupsData, storesData] = await Promise.all([
          fetchJsonCached('/api/admin/roles/options', { ttlMs: 300_000 }),
          groupsPromise,
          fetchJsonCached('/api/admin/roles/stores', { ttlMs: 300_000 }),
        ]);
        setRoleOptions(Array.isArray(rolesData) && rolesData.length ? rolesData : staticRoleOptions);
        setGroups(Array.isArray(groupsData) ? groupsData : []);
        setStores(Array.isArray(storesData) ? storesData : []);
      } catch {
        // keep previous lookup values if transient request fails
      }
    };
    const id = window.setInterval(refreshLookups, 120_000);
    return () => {
      window.clearInterval(id);
    };
  }, [canSee, isPureStoreAdmin, isPureMasterSalesStaff]);

  useEffect(() => {
    if (!createOpen) return;
    setCreateAttempted(false);
    if (isPureStoreAdmin || isPureMasterSalesStaff) {
      setCreateDraft((current) => ({
        ...current,
        groupId: actorGroupIdStr,
        storeId: actorStoreIdStr,
        roleCode: 'SALES_STAFF',
      }));
      return;
    }

    if (isGroupScopeAdmin) {
      setCreateDraft((current) => ({
        ...current,
        groupId: actorGroupIdStr,
      }));
      return;
    }

    if (isHkAdmin) {
      setCreateDraft((current) => ({
        ...current,
        groupId: '',
        storeId: '',
      }));
    }
  }, [createOpen, isPureStoreAdmin, isPureMasterSalesStaff, isGroupScopeAdmin, isHkAdmin, actorGroupIdStr, actorStoreIdStr]);

  const getRoleRank = (roleCode) => {
    const code = String(roleCode || '').toUpperCase();
    // Higher = more privilege (must match backend PortalRoleCodes.GetRoleRank).
    if (code === 'HK_SUPPORT') return 7;
    if (code === 'GROUP_ADMIN') return 6;
    if (code === 'SUPPORT') return 5;
    if (code === 'CUSTOMER_MASTER') return 4;
    if (code === 'STORE_ADMIN') return 3;
    if (code === 'MASTER_SALES_STAFF') return 2;
    if (code === 'SALES_STAFF') return 1;
    return 0;
  };

  const actorEffectiveRoleCode = useMemo(() => {
    if (isHkAdmin) return 'HK_SUPPORT';
    if (isGroupAdmin) return 'GROUP_ADMIN';
    if (isCustomerMaster) return 'CUSTOMER_MASTER';
    if (isStoreAdmin) return 'STORE_ADMIN';
    if (isMasterSalesStaff) return 'MASTER_SALES_STAFF';
    return '';
  }, [isHkAdmin, isGroupAdmin, isCustomerMaster, isStoreAdmin, isMasterSalesStaff]);

  const visibleRoleOptions = useMemo(() => {
    const list = Array.isArray(roleOptions) ? roleOptions : [];
    if (isHkAdmin) return list;
    const actorRank = getRoleRank(actorEffectiveRoleCode);
    if (!actorRank) return list;
    const hkOnly = new Set(['HK_SUPPORT', 'SUPPORT']);
    return list.filter((r) => {
      const code = String(r.code || '').toUpperCase();
      if (hkOnly.has(code)) return false;
      return getRoleRank(code) <= actorRank;
    });
  }, [roleOptions, isHkAdmin, actorEffectiveRoleCode]);

  const allowedCreateRoleOptions = useMemo(() => visibleRoleOptions, [visibleRoleOptions]);

  const changeRoleOptions = useMemo(
    () => visibleRoleOptions.filter((role) => String(role.code || '').toUpperCase() !== 'CUSTOMER_MASTER'),
    [visibleRoleOptions]
  );

  const createRoleSelectOptions = useMemo(
    () => buildGroupedRoleSelectOptions(allowedCreateRoleOptions),
    [allowedCreateRoleOptions]
  );

  const changeRoleSelectOptions = useMemo(
    () => buildGroupedRoleSelectOptions(changeRoleOptions),
    [changeRoleOptions]
  );

  const canChangeRoleEntry = (entry) => {
    if (!entry || isCurrentUserEntry(entry) || isCustomerMasterEntry(entry)) return false;
    const actorRank = getRoleRank(actorEffectiveRoleCode);
    const targetRank = getRoleRank(entry?.roleCode || getPrimaryRoleCode(entry));
    return actorRank > 0 && targetRank > 0 && actorRank > targetRank;
  };

  const canDeleteEntry = (entry) => {
    const actorRank = getRoleRank(actorEffectiveRoleCode);
    if (!actorRank) return false;

    const scopeOnly = willRemoveScopeOnly(entry);
    const rowRank = getRoleRank(entry?.roleCode || getPrimaryRoleCode(entry));
    if (!rowRank) return false;

    // Primary (highest) role record: only a strictly higher actor may fully delete it.
    if (entry?.__isPrimaryRow || !scopeOnly) {
      return actorRank > getRoleRank(getPrimaryRoleCode(entry));
    }

    // Secondary group/store row (incl. own Sales Staff under HK Administrator).
    if (actorEffectiveRoleCode === 'HK_SUPPORT') return true;
    return actorRank >= rowRank;
  };

  /** Own account: allow ⋯ only to remove a secondary scope; other users get the full menu. */
  const canOpenRowActionsMenu = (entry) => {
    if (!isCurrentUserEntry(entry)) return true;
    return canDeleteEntry(entry) && willRemoveScopeOnly(entry);
  };

  useEffect(() => {
    if (!createOpen) return;
    const roleCode = String(createDraft.roleCode || '').toUpperCase();
    if ((roleCode === 'GROUP_ADMIN' || roleCode === 'SUPPORT') && createDraft.storeId) {
      setCreateDraft((current) => ({ ...current, storeId: '' }));
    }
    if (roleCode === 'HK_SUPPORT' && (createDraft.groupId || createDraft.storeId)) {
      setCreateDraft((current) => ({ ...current, groupId: '', storeId: '' }));
    }
    if (roleCode !== 'SUPPORT' && createSupportGroupIds.length > 0) {
      setCreateSupportGroupIds([]);
    }
  }, [createOpen, createDraft.roleCode, createDraft.storeId, createDraft.groupId, createSupportGroupIds.length]);

  useEffect(() => {
    const onDocMouseDown = (event) => {
      if (createSupportGroupsPickerRef.current && !createSupportGroupsPickerRef.current.contains(event.target)) {
        setCreateSupportGroupsPickerOpen(false);
      }
      if (supportGroupsPickerRef.current && !supportGroupsPickerRef.current.contains(event.target)) {
        setSupportGroupsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const createGroupOptions = useMemo(() => {
    if (isPureStoreAdmin || isGroupScopeAdmin) {
      if (!actorGroupIdStr) {
        return [{ value: '', label: 'Missing group scope' }];
      }
      return [{ value: actorGroupIdStr, label: actorGroupLabel }];
    }
    return [{ value: '', label: 'No group scope' }, ...groups.map((g) => ({ value: String(g.id), label: g.name }))];
  }, [isPureStoreAdmin, isGroupScopeAdmin, actorGroupIdStr, actorGroupLabel, groups]);

  useEffect(() => {
    if (!createOpen || isPureStoreAdmin || isPureMasterSalesStaff) return undefined;
    if (!createDraft.groupId) {
      setCreateGroupStores([]);
      return undefined;
    }
    const controller = new AbortController();
    fetch(`/api/admin/roles/stores?groupId=${encodeURIComponent(createDraft.groupId)}`, {
      credentials: 'include',
      signal: controller.signal,
    })
      .then((response) => response.json().catch(() => []))
      .then((data) => setCreateGroupStores(Array.isArray(data) ? data : []))
      .catch(() => setCreateGroupStores([]));
    return () => controller.abort();
  }, [createOpen, createDraft.groupId, isPureStoreAdmin, isPureMasterSalesStaff]);

  const filteredCreateSupportGroups = useMemo(() => {
    const query = createSupportGroupsPickerQuery.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter((g) => String(g.name || '').toLowerCase().includes(query));
  }, [groups, createSupportGroupsPickerQuery]);

  const filteredSupportGroupsForModal = useMemo(() => {
    const query = supportGroupsPickerQuery.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter((g) => String(g.name || '').toLowerCase().includes(query));
  }, [groups, supportGroupsPickerQuery]);

  const createStoreOptions = useMemo(() => {
    if (createDraft.roleCode === 'GROUP_ADMIN' || createDraft.roleCode === 'SUPPORT') {
      return [{ value: '', label: 'All' }];
    }
    if (isPureStoreAdmin || isPureMasterSalesStaff) {
      if (!actorStoreIdStr) {
        return [{ value: '', label: 'Missing store scope' }];
      }
      return [{ value: actorStoreIdStr, label: actorStoreLabel }];
    }
    if (!createDraft.groupId) {
      return [{ value: '', label: 'Select group first' }];
    }
    return [{ value: '', label: 'Select store' }, ...createGroupStores.map((s) => ({ value: String(s.id), label: s.name }))];
  }, [createDraft.roleCode, createDraft.groupId, isPureStoreAdmin, isPureMasterSalesStaff, actorStoreIdStr, actorStoreLabel, createGroupStores]);

  const isCreateStoreLocked = useMemo(() => {
    const roleCode = String(createDraft.roleCode || '').toUpperCase();
    return (
      isPureStoreAdmin
      || isPureMasterSalesStaff
      || roleCode === 'GROUP_ADMIN'
      || roleCode === 'SUPPORT'
      || (!isPureStoreAdmin && !createDraft.groupId && roleCode !== 'SUPPORT')
    );
  }, [createDraft.roleCode, createDraft.groupId, isPureStoreAdmin, isPureMasterSalesStaff]);

  const normalizeCreateUserError = (message) => {
    const raw = String(message || '').trim();
    if (!raw) return 'Create user failed.';

    if (/INVALID_IDENTITY_EMAIL/i.test(raw) || /Invalid email provided for identity/i.test(raw)) {
      return 'Please enter a valid email address (e.g. name@company.com).';
    }
    if (/USER_NOT_CREATED/i.test(raw)) {
      return 'Kinde could not create the user. Please verify the email address and try again.';
    }
    if (/already has this role/i.test(raw)) {
      return 'This user already has the selected role in that group or store.';
    }
    if (/USER_ALREADY_EXISTS|existing user was found/i.test(raw)) {
      return 'This email is already registered. The user was linked to your group instead of creating a duplicate account.';
    }
    if (/Kinde create user failed/i.test(raw) && /400/i.test(raw) && /email/i.test(raw)) {
      return 'Kinde rejected the email address. Please check the format (e.g. name@company.com).';
    }

    // Avoid dumping huge JSON blobs into toasts.
    if (raw.includes('{') && raw.length > 220) {
      return 'Create user failed. Please check the entered email and try again.';
    }

    return raw;
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, roleFilter]);

  const onCreateUser = async () => {
    setCreateAttempted(true);
    const nextRoleCode = String(createDraft.roleCode || '').trim().toUpperCase();
    const isCustomerMasterRole = nextRoleCode === 'CUSTOMER_MASTER';

    if (isCustomerMasterRole) {
      await onCreateCustomerMaster();
      return;
    }

    const firstName = createDraft.firstName.trim();
    const lastName = createDraft.lastName.trim();
    if (!firstName) {
      setError('First name is required.');
      return;
    }
    if (!lastName) {
      setError('Last name is required.');
      return;
    }
    const email = createDraft.email.trim();
    if (!email) {
      setError('Email is required.');
      return;
    }
    const isEmailFormatOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && email.length <= 254;
    if (!isEmailFormatOk) {
      setError('Please enter a valid email address (e.g. name@company.com).');
      return;
    }

    if ((isPureStoreAdmin || isPureMasterSalesStaff) && (!createDraft.groupId || !createDraft.storeId)) {
      setError('Your admin account is missing group/store scope. Please contact HK Support.');
      return;
    }

    const nextRole = String(createDraft.roleCode || '').toUpperCase();
    const allowedCodes = new Set(
      (Array.isArray(allowedCreateRoleOptions) ? allowedCreateRoleOptions : [])
        .map((r) => String(r.code || '').toUpperCase())
        .filter(Boolean),
    );
    if (!allowedCodes.has(nextRole)) {
      setError('You cannot assign a role above your own access level.');
      return;
    }

    if (nextRole === 'SUPPORT') {
      if (!isHkAdmin) {
        setError('Only HK Administrator can assign HK Support role.');
        return;
      }
      if (createSupportGroupIds.length === 0) {
        setError('Select at least one group for Support role.');
        return;
      }
    }

    const password = String(createDraft.password || '').trim();
    const confirmPassword = String(createDraft.confirmPassword || '').trim();
    if (password || confirmPassword) {
      if (password.length < 8) {
        setError('Password must be at least 8 characters.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Password confirmation does not match.');
        return;
      }
    }

    setIsSaving(true);
    setError('');
    try {
      const response = await fetch('/api/admin/roles/users', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...createDraft,
          firstName,
          lastName,
          password: password || null,
          confirmPassword: undefined,
          groupId: nextRoleCode === 'HK_SUPPORT'
            ? null
            : nextRoleCode === 'SUPPORT'
              ? (createSupportGroupIds[0] ? Number(createSupportGroupIds[0]) : null)
              : (createDraft.groupId ? Number(createDraft.groupId) : null),
          groupIds: nextRoleCode === 'SUPPORT' ? createSupportGroupIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0) : undefined,
          storeId: nextRoleCode === 'HK_SUPPORT' || nextRoleCode === 'SUPPORT' || nextRoleCode === 'GROUP_ADMIN'
            ? null
            : (createDraft.storeId ? Number(createDraft.storeId) : null),
          isActive: true,
        }),
      });

      if (!response.ok) {
        const maybeError = await response.json().catch(() => null);
        throw new Error(normalizeCreateUserError(maybeError?.message));
      }

      const createdPayload = await response.json().catch(() => ({}));
      setSuccess(createdPayload?.scopeAppended ? 'User assigned to the selected group.' : 'User created.');
      setCreateOpen(false);
      setCreateSupportGroupIds([]);
      setCreateSupportGroupsPickerOpen(false);
      setCreateSupportGroupsPickerQuery('');
      setCreateDraft({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        roleCode: 'SALES_STAFF',
        groupId: isPureStoreAdmin || isPureMasterSalesStaff || isGroupScopeAdmin ? actorGroupIdStr : '',
        storeId: isPureStoreAdmin || isPureMasterSalesStaff ? actorStoreIdStr : '',
        password: '',
        confirmPassword: '',
      });

      const updated = await fetchAllUsersPayload();
      applyUsersPayload(updated);
    } catch (createError) {
      setError(normalizeCreateUserError(createError.message));
    } finally {
      setIsSaving(false);
    }
  };

  const onCreateCustomerMaster = async () => {
    if (!canManageCustomerMasters) {
      setError('Only HK Administrator and Group-Admin can create Customer Master users.');
      return;
    }

    const customerAccountId = Number(generalDraft.customerAccountId);
    const login = generalDraft.login.trim();
    const password = generalDraft.password;
    const email = generalDraft.email.trim();
    if (!Number.isFinite(customerAccountId) || customerAccountId <= 0) {
      setError('CustomerAccountId is required.');
      return;
    }
    if (!login) {
      setError('Login is required.');
      return;
    }
    if (!/^[A-Za-z0-9_-]{5,64}$/.test(login)) {
      setError('Login must be 5-64 chars: letters, numbers, "-" or "_".');
      return;
    }
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Email is invalid.');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const response = await fetch('/api/admin/customer-account-general-users', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerAccountId,
          login,
          password,
          email: email || undefined,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to create general user.');
      }

      setSuccess('Customer Master created.');
      setCreateOpen(false);
      setCreateSupportGroupIds([]);
      setCreateSupportGroupsPickerOpen(false);
      setCreateSupportGroupsPickerQuery('');
      setCreateDraft({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        roleCode: 'SALES_STAFF',
        groupId: isPureStoreAdmin || isPureMasterSalesStaff || isGroupScopeAdmin ? actorGroupIdStr : '',
        storeId: isPureStoreAdmin || isPureMasterSalesStaff ? actorStoreIdStr : '',
        password: '',
        confirmPassword: '',
      });
      setGeneralDraft({ customerAccountId: '', login: '', password: '', email: '' });
      const updated = await fetchAllUsersPayload();
      applyUsersPayload(updated);
    } catch (createError) {
      setError(createError.message || 'Failed to create general user.');
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!createOpen || !canManageCustomerMasters) return;
    if (String(createDraft.roleCode || '').toUpperCase() !== 'CUSTOMER_MASTER') return;
    let disposed = false;
    const controller = new AbortController();
    const load = async () => {
      try {
        const data = await fetchJsonCached('/api/admin/customer-account-general-users/customer-accounts', {
          ttlMs: 0,
          signal: controller.signal,
        });
        if (!disposed) {
          setGeneralCustomerAccounts(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!disposed) {
          setGeneralCustomerAccounts([]);
        }
      }
    };
    load();
    return () => {
      disposed = true;
      controller.abort();
    };
  }, [createOpen, canManageCustomerMasters, createDraft.roleCode]);

  const onToggleUserStatus = async (entry) => {
    setError('');
    setSuccess('');
    setIsSaving(true);
    try {
      const response = await fetch(`/api/admin/roles/users/${entry.userId}/status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !entry.isActive }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to update status.');
      }

      setUsers((current) =>
        current.map((userItem) =>
          userItem.userId === entry.userId
            ? { ...userItem, isActive: !entry.isActive }
            : userItem
        )
      );
      setSuccess(!entry.isActive ? 'User unblocked.' : 'User blocked.');
    } catch (statusError) {
      setError(statusError.message || 'Failed to update status.');
    } finally {
      setIsSaving(false);
    }
  };

  const onResetPassword = async (entry) => {
    setError('');
    setSuccess('');
    setIsActionBusy(true);
    try {
      const response = await fetch(`/api/admin/roles/users/${entry.userId}/password-reset`, { method: 'POST', credentials: 'include' });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to request password reset.');
      }
      setSuccess('Password reset requested. The user will be prompted to change password on next sign-in.');
    } catch (resetError) {
      setError(resetError.message || 'Failed to request password reset.');
    } finally {
      setIsActionBusy(false);
    }
  };

  const onDeleteUser = async (entry) => {
    setError('');
    setSuccess('');
    setIsActionBusy(true);
    try {
      const params = new URLSearchParams();
      if (entry.groupId != null) params.set('groupId', String(entry.groupId));
      const query = params.toString();
      const response = await fetch(
        `/api/admin/roles/users/${entry.userId}${query ? `?${query}` : ''}`,
        { method: 'DELETE', credentials: 'include' }
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to delete user.');
      }
      if (payload?.mode === 'scope') {
        setSuccess('Group access removed.');
        const updated = await fetchAllUsersPayload();
        applyUsersPayload(updated);
      } else {
        setSuccess('User deleted.');
        setUsers((current) => current.filter((u) => u.userId !== entry.userId));
      }
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete user.');
    } finally {
      setIsActionBusy(false);
    }
  };

  const isCustomerMasterEntry = (entry) => {
    const roleCode = String(entry?.roleCode || '').toUpperCase();
    return roleCode === 'CUSTOMER_MASTER';
  };

  const isStandardUserEntry = (entry) => String(entry?.roleCode || '').toUpperCase() === 'SALES_STAFF';

  const onChangeCustomerMasterPassword = async (entry, password) => {
    setError('');
    setSuccess('');
    setIsActionBusy(true);
    try {
      const response = await fetch(`/api/admin/customer-account-general-users/${entry.userId}/password`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to update general user password.');
      }
      setSuccess('Customer Master password updated.');
      return true;
    } catch (changeError) {
      setError(changeError.message || 'Failed to update general user password.');
      return false;
    } finally {
      setIsActionBusy(false);
    }
  };

  const onOpenRoleModal = (entry) => {
    const roleCode = String(entry?.roleCode || 'SALES_STAFF').toUpperCase();
    const fallback = changeRoleOptions[0]?.code || 'SALES_STAFF';
    const initialRole = changeRoleOptions.some((r) => String(r.code).toUpperCase() === roleCode)
      ? roleCode
      : fallback;
    setRoleModal({ entry });
    setRoleDraft({
      roleCode: initialRole,
      groupId: entry?.groupId ? String(entry.groupId) : '',
      storeId: entry?.storeId ? String(entry.storeId) : '',
    });
    setRoleModalStores([]);
  };

  const onCloseRoleModal = () => {
    setRoleModal(null);
    setRoleDraft({ roleCode: '', groupId: '', storeId: '' });
    setRoleModalStores([]);
    setIsRoleSaving(false);
  };

  const roleRequiresGroup = ['GROUP_ADMIN', 'SUPPORT'].includes(String(roleDraft.roleCode || '').toUpperCase());
  const roleRequiresStore = String(roleDraft.roleCode || '').toUpperCase() === 'STORE_ADMIN';

  useEffect(() => {
    if (!roleModal) return undefined;
    const roleCode = String(roleDraft.roleCode || '').toUpperCase();
    if (roleCode === 'HK_SUPPORT') {
      if (roleDraft.groupId || roleDraft.storeId) {
        setRoleDraft((current) => ({ ...current, groupId: '', storeId: '' }));
      }
      return undefined;
    }
    if ((roleCode === 'GROUP_ADMIN' || roleCode === 'SUPPORT') && roleDraft.storeId) {
      setRoleDraft((current) => ({ ...current, storeId: '' }));
    }
    return undefined;
  }, [roleModal, roleDraft.roleCode, roleDraft.groupId, roleDraft.storeId]);

  useEffect(() => {
    if (!roleModal || !roleRequiresStore) {
      setRoleModalStores([]);
      return undefined;
    }

    let cancelled = false;
    const groupId = roleDraft.groupId;
    const url = groupId
      ? `/api/admin/roles/stores?groupId=${encodeURIComponent(groupId)}`
      : '/api/admin/roles/stores';

    fetch(url, { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : []))
      .then((data) => {
        if (!cancelled) setRoleModalStores(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setRoleModalStores([]);
      });

    return () => {
      cancelled = true;
    };
  }, [roleModal, roleRequiresStore, roleDraft.groupId]);

  const onSaveRoleChange = async () => {
    if (!roleModal?.entry || !roleDraft.roleCode) return;
    const nextRole = String(roleDraft.roleCode || '').toUpperCase();
    if (nextRole === 'CUSTOMER_MASTER') {
      setError('Customer Master cannot be assigned via Change Role.');
      return;
    }
    if (roleRequiresGroup && !roleDraft.groupId) {
      setError(nextRole === 'SUPPORT' ? 'Select at least one group for HK Support.' : 'Select a group for Group-Admin.');
      return;
    }
    if (roleRequiresStore && !roleDraft.storeId) {
      setError('Select a store for Store-Admin.');
      return;
    }

    setIsRoleSaving(true);
    setError('');
    setSuccess('');
    try {
      const entry = roleModal.entry;
      const body = {
        roleCode: nextRole,
        groupId: roleDraft.groupId ? Number(roleDraft.groupId) : null,
        storeId: roleDraft.storeId ? Number(roleDraft.storeId) : null,
        previousRoleCode: String(entry?.roleCode || '').toUpperCase() || null,
        previousGroupId: entry?.groupId != null ? Number(entry.groupId) : null,
        previousStoreId: entry?.storeId != null ? Number(entry.storeId) : null,
      };
      if (nextRole === 'SUPPORT' && roleDraft.groupId) {
        body.groupIds = [Number(roleDraft.groupId)];
      }

      const response = await fetch(`/api/admin/roles/users/${entry.userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Role update failed.');
      }

      const updated = await fetchAllUsersPayload();
      applyUsersPayload(updated);
      setSuccess(`Role for ${entry.email} updated.`);
      onCloseRoleModal();
    } catch (saveError) {
      setError(saveError.message || 'Role update failed.');
    } finally {
      setIsRoleSaving(false);
    }
  };

  const onAssignMissingScope = async () => {
    if (!scopeModal?.entry) return;
    setError('');
    setSuccess('');
    setIsActionBusy(true);
    try {
      const response = await fetch(`/api/admin/roles/users/${scopeModal.entry.userId}/scope`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          roleCode: scopeModal.entry?.roleCode || null,
          groupId: scopeDraft.groupId ? Number(scopeDraft.groupId) : null,
          storeId: scopeDraft.storeId ? Number(scopeDraft.storeId) : null,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to assign group/store.');
      }

      const updated = await fetchAllUsersPayload();
      applyUsersPayload(updated);
      setSuccess('Group/Store assigned.');
      setScopeModal(null);
      setScopeDraft({ groupId: '', storeId: '' });
    } catch (scopeError) {
      setError(scopeError.message || 'Failed to assign group/store.');
    } finally {
      setIsActionBusy(false);
    }
  };

  const storesForScopeGroup = useMemo(() => (scopeStores ?? stores), [scopeStores, stores]);

  useEffect(() => {
    if (!scopeDraft.storeId) return;
    const exists = storesForScopeGroup.some((store) => String(store.id) === String(scopeDraft.storeId));
    if (!exists) {
      setScopeDraft((current) => ({ ...current, storeId: '' }));
    }
  }, [scopeDraft.storeId, storesForScopeGroup]);

  useEffect(() => {
    if (!scopeModal) return;
    const controller = new AbortController();
    const groupId = scopeDraft.groupId ? Number(scopeDraft.groupId) : null;
    const url = groupId ? `/api/admin/roles/stores?groupId=${groupId}` : '/api/admin/roles/stores';

    fetch(url, { credentials: 'include', signal: controller.signal })
      .then((response) => response.json().catch(() => []))
      .then((data) => {
        setScopeStores(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setScopeStores(groupId ? [] : stores);
      });

    return () => controller.abort();
  }, [scopeModal, scopeDraft.groupId, stores]);

  const openStoresModal = async (entry) => {
    setStoresModal({ entry });
    setSelectedStoreIds([]);
    setManageStores([]);
    setError('');
    setIsActionBusy(true);
    try {
      const groupId = entry?.groupId ? Number(entry.groupId) : null;
      const storesUrl = groupId ? `/api/admin/roles/stores?groupId=${groupId}` : '/api/admin/roles/stores';
      const latestStores = await fetchJsonCached(storesUrl, { ttlMs: 0 });
      const list = Array.isArray(latestStores) ? latestStores : [];
      setManageStores(list);
      const response = await fetch(`/api/admin/roles/users/${entry.userId}/stores?groupId=${encodeURIComponent(String(groupId || ''))}`, { credentials: 'include' });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to load user stores.');
      }
      const data = await response.json();
      const allowed = new Set(list.map((store) => store.id));
      setSelectedStoreIds(Array.isArray(data) ? data.map((x) => x.id).filter((id) => allowed.has(id)) : []);
    } catch (e) {
      setError(e.message || 'Failed to load user stores.');
    } finally {
      setIsActionBusy(false);
    }
  };

  const onSaveUserStores = async () => {
    if (!storesModal?.entry) return;
    setError('');
    setSuccess('');
    setIsActionBusy(true);
    try {
      const groupId = storesModal?.entry?.groupId ? Number(storesModal.entry.groupId) : null;
      const response = await fetch(`/api/admin/roles/users/${storesModal.entry.userId}/stores`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeIds: selectedStoreIds.map((id) => Number(id)), groupId }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to update user stores.');
      }

      const updated = await fetchAllUsersPayload();
      applyUsersPayload(updated);
      setStoresModal(null);
      setSuccess('User stores updated.');
    } catch (e) {
      setError(e.message || 'Failed to update user stores.');
    } finally {
      setIsActionBusy(false);
    }
  };

  const filteredStoresForManageModal = useMemo(() => {
    const q = storesModalSearch.trim().toLowerCase();
    if (!q) return manageStores;
    return manageStores.filter((store) => String(store?.name || '').toLowerCase().includes(q));
  }, [manageStores, storesModalSearch]);

  const onImportCsv = async () => {
    if (!importFile) {
      setError('Please select a file.');
      return;
    }
    setIsImporting(true);
    setError('');
    setSuccess('');
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', importFile);
      const response = await fetch('/api/admin/roles/users/import-csv', {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || 'Import failed.');
      }
      setImportResult(payload);
      setSuccess(`Import completed. Created: ${payload?.created ?? 0}, Failed: ${payload?.failed ?? 0}.`);
      const updated = await fetchAllUsersPayload();
      applyUsersPayload(updated);
    } catch (e) {
      setError(e.message || 'Import failed.');
    } finally {
      setIsImporting(false);
    }
  };

  const onToggleSort = (key) => {
    setSort((current) => {
      if (current.key !== key) return { key, dir: 'asc' };
      if (current.dir === 'asc') return { key, dir: 'desc' };
      if (current.dir === 'desc') return { key: '', dir: null };
      return { key, dir: 'asc' };
    });
  };

  const canManageStoresForEntry = (entry) => {
    const roleCode = String(entry?.roleCode || '').toUpperCase();
    return roleCode === 'SALES_STAFF';
  };

  const isCurrentUserEntry = (entry) =>
    String(entry?.email || '').trim().toLowerCase() === currentUserEmail;

  const actorGroupIdNum = useMemo(() => {
    if (user?.groupId != null) return Number(user.groupId);
    return null;
  }, [user?.groupId]);

  const actorGroupAdminGroupIds = useMemo(() => {
    const fromApi = Array.isArray(user?.groupAdminGroupIds)
      ? user.groupAdminGroupIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (fromApi.length > 0) return fromApi;
    const fromCustomerMaster = Array.isArray(user?.customerMasterGroupIds)
      ? user.customerMasterGroupIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (fromCustomerMaster.length > 0) return fromCustomerMaster;
    return Number.isFinite(actorGroupIdNum) ? [actorGroupIdNum] : [];
  }, [user?.groupAdminGroupIds, user?.customerMasterGroupIds, actorGroupIdNum]);

  const actorStoreAdminStoreIds = useMemo(() => {
    const fromApi = Array.isArray(user?.storeAdminStoreIds)
      ? user.storeAdminStoreIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (fromApi.length > 0) return fromApi;
    const storeIdNum = user?.storeId != null ? Number(user.storeId) : null;
    return Number.isFinite(storeIdNum) && storeIdNum > 0 ? [storeIdNum] : [];
  }, [user?.storeAdminStoreIds, user?.storeId]);

  const scopeFilterContext = useMemo(
    () => ({
      isHkAdmin,
      groupAdminGroupIds: actorGroupAdminGroupIds,
      storeAdminStoreIds: actorStoreAdminStoreIds,
    }),
    [isHkAdmin, actorGroupAdminGroupIds, actorStoreAdminStoreIds]
  );

  const visibleRows = useMemo(() => {
    const rows = [];
    for (const entry of users) {
      const primaryRoleCode = String(entry.roleCode || '').toUpperCase();
      const primaryRoleLabel = entry.roleLabel || roleCodeToLabel(primaryRoleCode || 'SALES_STAFF');
      const scopes = filterGroupScopesForActor(normalizeGroupScopes(entry), scopeFilterContext);
      if (!isHkAdmin && (isGroupScopeAdmin || isStoreAdmin) && scopes.length === 0 && primaryRoleCode !== 'HK_SUPPORT') {
        continue;
      }

      const isGlobalPrimary = primaryRoleCode === 'HK_SUPPORT';

      // Always surface the highest/global role as its own primary row (e.g. HK Administrator),
      // even when the user also has store/group scopes.
      if (isGlobalPrimary || scopes.length === 0) {
        const resolved = primaryRoleCode === 'CUSTOMER_MASTER'
          ? { roleCode: 'CUSTOMER_MASTER', roleLabel: 'Customer Master' }
          : {
              roleCode: primaryRoleCode || 'SALES_STAFF',
              roleLabel: primaryRoleLabel,
            };
        rows.push({
          ...entry,
          __rowKey: `${entry.userId}:primary`,
          __primaryRoleCode: primaryRoleCode,
          __isPrimaryRow: true,
          groupId: isGlobalPrimary ? null : entry.groupId,
          groupName: isGlobalPrimary ? '' : entry.groupName,
          storeId: isGlobalPrimary ? null : entry.storeId,
          storeName: isGlobalPrimary ? '' : (primaryRoleCode === 'CUSTOMER_MASTER' ? 'All' : entry.storeName),
          customerAccountName: isGlobalPrimary ? '' : entry.customerAccountName,
          roleCode: resolved.roleCode,
          roleLabel: resolved.roleLabel,
        });
      }

      let primaryScopeIndex = -1;
      if (!isGlobalPrimary && scopes.length > 0) {
        let maxRank = -1;
        scopes.forEach((s, index) => {
          const { roleCode: scopeRoleCode } = resolveScopeRole(s, entry);
          const rank = getRoleRank(scopeRoleCode);
          if (rank > maxRank) {
            maxRank = rank;
            primaryScopeIndex = index;
          }
        });
      }

      scopes.forEach((s, index) => {
        const { roleCode: scopeRoleCode, roleLabel: scopeRoleLabel } = resolveScopeRole(s, entry);
        const isScopeGroupWide = isGroupWideRoleEntry({ roleCode: scopeRoleCode }) || scopeRoleCode === 'CUSTOMER_MASTER';
        const scopeRank = getRoleRank(scopeRoleCode);
        const primaryRank = getRoleRank(primaryRoleCode);
        // Skip a scope row that duplicates the global primary HK Administrator row.
        if (isGlobalPrimary && scopeRank >= primaryRank) return;
        rows.push({
          ...entry,
          __rowKey: `${entry.userId}:g:${s.groupId}`,
          __primaryRoleCode: primaryRoleCode,
          __isPrimaryRow: !isGlobalPrimary && index === primaryScopeIndex,
          groupId: s.groupId,
          groupName: s.groupName || entry.groupName,
          roleCode: scopeRoleCode,
          roleLabel: scopeRoleLabel,
          customerAccountId: isScopeGroupWide && scopeRoleCode === 'GROUP_ADMIN' ? null : s.customerAccountId,
          customerAccountName: isGroupWideRoleEntry({ roleCode: scopeRoleCode }) ? 'All' : (s.customerAccountName || ''),
          storeId: isScopeGroupWide ? null : s.storeId,
          storeName: isScopeGroupWide ? 'All' : (s.storeName || ''),
        });
      });
    }
    return rows;
  }, [users, isHkAdmin, isGroupScopeAdmin, isStoreAdmin, scopeFilterContext]);

  const columnFilterOptions = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(COLUMN_FILTER_GETTERS).map(([key, getValue]) => [
          key,
          buildColumnFilterOptions(visibleRows, getValue),
        ])
      ),
    [visibleRows]
  );

  const filteredUsers = useMemo(
    () => visibleRows.filter((entry) => passesColumnFilters(entry, columnFilters, columnFilterOptions)),
    [visibleRows, columnFilters, columnFilterOptions]
  );

  const filteredUsersCount = filteredUsers.length;
  const columnFiltersLimited = usersRemoteTotal > users.length;

  const sortedUsers = useMemo(() => {
    if (!sort?.dir || !sort?.key) return filteredUsers;

    const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });

    const valueOf = (entry) => {
      if (sort.key === 'user') return getUserDisplayName(entry);
      if (sort.key === 'email') return entry.email;
      if (sort.key === 'status') return entry.statusLabel || (entry.isActive ? 'Active' : 'Inactive');
      if (sort.key === 'role') return entry.roleLabel || entry.roleCode;
      if (sort.key === 'customer') return formatUserCustomerLabel(entry);
      if (sort.key === 'group') return formatUserGroupLabel(entry);
      if (sort.key === 'store') return formatUserStoreLabel(entry);
      return '';
    };

    const next = [...filteredUsers].sort((left, right) => {
      if (sort.key === 'status') {
        const rank = (entry) => {
          const label = String(entry.statusLabel || '').toLowerCase();
          if (label === 'unverified') return 0;
          return entry.isActive ? 1 : 2;
        };
        const leftRank = rank(left);
        const rightRank = rank(right);
        return leftRank - rightRank;
      }

      return compareText(valueOf(left), valueOf(right));
    });

    return sort.dir === 'desc' ? next.reverse() : next;
  }, [filteredUsers, sort]);

  const openRowActionsMenu = (entry, targetElement) => {
    if (!targetElement) return;
    const rect = targetElement.getBoundingClientRect();
    const estimatedMenuHeight = 240;
    const estimatedMenuWidth = 190;
    const viewportPadding = 8;
    const gap = 8;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const spaceBelow = viewportHeight - rect.bottom - viewportPadding;
    const openAbove = spaceBelow < estimatedMenuHeight && rect.top > spaceBelow;
    const top = openAbove
      ? Math.max(viewportPadding, rect.top - estimatedMenuHeight - gap)
      : Math.min(viewportHeight - estimatedMenuHeight - viewportPadding, rect.bottom + gap);
    const left = Math.max(
      viewportPadding,
      Math.min(rect.right - estimatedMenuWidth, viewportWidth - estimatedMenuWidth - viewportPadding)
    );

    setOpenMenuFor(entry.__rowKey);
    setRowMenuAnchorRect(rect);
    setRowMenuPos({
      top: Math.max(viewportPadding, top),
      left,
    });
  };

  useEffect(() => {
    if (!openMenuFor || !rowMenuAnchorRect) return undefined;

    const recomputeMenuPos = () => {
      const rect = rowMenuAnchorRect;
      const menuEl = rowMenuPortalRef.current;
      const viewportPadding = 8;
      const gap = 8;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const menuHeight = Math.max(120, menuEl?.offsetHeight || 240);
      const menuWidth = Math.max(190, menuEl?.offsetWidth || 190);
      const spaceBelow = viewportHeight - rect.bottom - viewportPadding;
      const openAbove = spaceBelow < menuHeight && rect.top > spaceBelow;
      const top = openAbove
        ? Math.max(viewportPadding, rect.top - menuHeight - gap)
        : Math.min(viewportHeight - menuHeight - viewportPadding, rect.bottom + gap);
      const left = Math.max(
        viewportPadding,
        Math.min(rect.right - menuWidth, viewportWidth - menuWidth - viewportPadding)
      );

      setRowMenuPos({ top: Math.max(viewportPadding, top), left });
    };

    recomputeMenuPos();
    const rafId = window.requestAnimationFrame(recomputeMenuPos);
    window.addEventListener('resize', recomputeMenuPos);
    window.addEventListener('scroll', recomputeMenuPos, true);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', recomputeMenuPos);
      window.removeEventListener('scroll', recomputeMenuPos, true);
    };
  }, [openMenuFor, rowMenuAnchorRect]);

  const activeMenuEntry = useMemo(
    () => visibleRows.find((entry) => entry.__rowKey === openMenuFor) || null,
    [visibleRows, openMenuFor]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(Math.max(0, filteredUsersCount) / PAGE_SIZE)),
    [filteredUsersCount]
  );

  useEffect(() => {
    setCurrentPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedUsers.slice(start, start + PAGE_SIZE);
  }, [sortedUsers, currentPage]);

  const visiblePageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
    return Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);
  }, [currentPage, totalPages]);

  if (!canSee) {
    return (
      <motion.section className="panel-card">
        <h2>Users access required</h2>
        <p className="hint-text">Only HK Administrator, Group-Admin, Store-Admin or Master Sales Staff can access users list.</p>
      </motion.section>
    );
  }

  return (
    <section className="admin-stack">
      <motion.article
        className="panel-card users-panel"
        ref={menuRootRef}
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="panel-title users-panel-title">
          <div className="users-panel-title-left">
            <Users size={16} />
            <h2>Users</h2>
          </div>
          <div className="users-panel-actions">
            <button
              type="button"
              className="ghost-button admin-inline-button compact"
              onClick={() => {
                setImportOpen(true);
                setImportResult(null);
                setImportFile(null);
              }}
            >
              <Upload size={14} />
              Import
            </button>
            <button
              type="button"
              className="primary-button admin-inline-button"
              onClick={() => {
                setCreateDraft({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phoneNumber: '',
                  roleCode: 'SALES_STAFF',
                  groupId: isPureStoreAdmin || isGroupScopeAdmin ? actorGroupIdStr : '',
                  storeId: isPureStoreAdmin ? actorStoreIdStr : '',
                  password: '',
                  confirmPassword: '',
                });
                setGeneralDraft({ customerAccountId: '', login: '', password: '', email: '' });
                setCreateAttempted(false);
                setCreateOpen(true);
              }}
            >
              <Plus size={14} />
              Add user
            </button>
          </div>
        </div>

        <div className="admin-filters">
          <div className="search-input-wrap">
            <Search size={14} />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by user or email" />
          </div>
          <RoundedSelect
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
            options={[
              { value: 'All', label: 'All statuses' },
              { value: 'Active', label: 'Active' },
              { value: 'Inactive', label: 'Inactive' },
            ]}
          />
          <RoundedSelect
            value={roleFilter}
            onChange={(val) => setRoleFilter(val)}
            options={[
              { value: 'All', label: 'All roles' },
              ...roleOptions.map((role) => ({ value: role.code, label: role.label })),
            ]}
          />
        </div>

        {isLoading && users.length === 0 ? (
          <div className="portal-loading-inline">Loading users...</div>
        ) : (
          <div className="table-wrap">
            <table className="permissions-table">
              <thead>
                <tr>
                  {[
                    { key: 'user', label: 'User' },
                    { key: 'email', label: 'Email' },
                    { key: 'status', label: 'Status' },
                    { key: 'role', label: 'Role' },
                    { key: 'group', label: 'Group' },
                    { key: 'customer', label: 'Customer' },
                    { key: 'store', label: 'Store' },
                  ].map((column) => (
                    <th key={column.key}>
                      <div className="table-th-content">
                        <TableColumnFilter
                          columnKey={column.key}
                          label={column.label}
                          options={columnFilterOptions[column.key] || []}
                          selectedValues={columnFilters[column.key]}
                          onChange={onColumnFilterChange}
                          disabled={isLoading || users.length === 0}
                        />
                        <button type="button" className="sortable-th-button" onClick={() => onToggleSort(column.key)}>
                          {column.label}{' '}
                          <span
                            className={
                              sort.key === column.key ? `sort-indicator ${sort.dir || 'none'}` : 'sort-indicator none'
                            }
                          />
                        </button>
                      </div>
                    </th>
                  ))}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && users.length > 0 && pagedUsers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="table-empty-filtered">
                      No users match the selected column filters.
                    </td>
                  </tr>
                )}
                {pagedUsers.map((entry, index) => (
                  <motion.tr
                    key={entry.__rowKey}
                    className="users-table-row"
                    initial={usersRowInitial(index)}
                    animate={usersRowAnimate}
                    transition={usersRowTransition(index)}
                  >
                    <td data-label="User">
                      <div className="users-name-cell">
                        <UserAvatar entry={entry} />
                        <span>{[entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'No name'}</span>
                        {String(entry.email || '').trim().toLowerCase() === currentUserEmail ? (
                          <span className="current-user-badge">You</span>
                        ) : null}
                      </div>
                    </td>
                    <td data-label="Email">{entry.email}</td>
                    <td data-label="Status">{entry.statusLabel || (entry.isActive ? 'Active' : 'Inactive')}</td>
                    <td data-label="Role">{entry.roleLabel}</td>
                    <td data-label="Group">{formatUserGroupLabel(entry) || '—'}</td>
                    <td data-label="Customer">{formatUserCustomerLabel(entry) || '—'}</td>
                    <td data-label="Store">{formatUserStoreLabel(entry) || '—'}</td>
                    <td data-label="Actions">
                      <div className="row-actions">
                        <button
                          type="button"
                          className={`ghost-button user-status-button ${entry.isActive ? 'danger' : 'safe'}`}
                          onClick={() => onToggleUserStatus(entry)}
                          disabled={isSaving || isCurrentUserEntry(entry)}
                          title={isCurrentUserEntry(entry) ? 'You cannot block your own account.' : undefined}
                        >
                          {entry.isActive ? 'Block' : 'Unblock'}
                        </button>
                        <button
                          type="button"
                          className="ellipsis-button"
                          aria-haspopup="menu"
                          aria-expanded={openMenuFor === entry.__rowKey}
                          onClick={(event) => {
                            if (!canOpenRowActionsMenu(entry)) return;
                            if (openMenuFor === entry.__rowKey) {
                              setOpenMenuFor(0);
                              setRowMenuPos(null);
                              return;
                            }
                            openRowActionsMenu(entry, event.currentTarget);
                          }}
                          disabled={isSaving || isActionBusy || !canOpenRowActionsMenu(entry)}
                          title={
                            isCurrentUserEntry(entry) && !canOpenRowActionsMenu(entry)
                              ? 'You can only remove a secondary role from your own account.'
                              : 'Actions'
                          }
                        >
                          ⋯
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {columnFiltersLimited && !isLoading && users.length > 0 && (
          <p className="table-column-filter-hint">
            Column filter counts are based on the first {users.length} of {usersRemoteTotal} users. Narrow search or status/role filters to refine.
          </p>
        )}

        {!isLoading && (filteredUsersCount > 0 || users.length > 0) && (
          <div className="table-pager">
            <button
              type="button"
              className="ghost-button admin-inline-button"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <div className="table-pager-pages">
              {visiblePageNumbers.map((page, index) => {
                const prev = visiblePageNumbers[index - 1];
                const hasGap = typeof prev === 'number' && page - prev > 1;
                return (
                  <React.Fragment key={page}>
                    {hasGap && <span className="pager-gap">…</span>}
                    <button
                      type="button"
                      className={page === currentPage ? 'pager-page active' : 'pager-page'}
                      onClick={() => setCurrentPage(page)}
                      aria-current={page === currentPage ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
            <div className="table-pager-info">
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <small>
                {filteredUsersCount} user{filteredUsersCount === 1 ? '' : 's'}
                {columnFiltersLimited ? ` (of ${usersRemoteTotal} total)` : ''}
              </small>
            </div>
            <button
              type="button"
              className="ghost-button admin-inline-button"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        )}
      </motion.article>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence mode="wait">
          {openMenuFor && activeMenuEntry && rowMenuPos && (
            <motion.div
              ref={rowMenuPortalRef}
              className="row-action-menu"
              role="menu"
              style={{ position: 'fixed', top: rowMenuPos.top, left: rowMenuPos.left, right: 'auto' }}
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              {!isCurrentUserEntry(activeMenuEntry) && !isCustomerMasterEntry(activeMenuEntry) && (
                <button
                  type="button"
                  className="row-action-item"
                  onClick={() => {
                    setOpenMenuFor(0);
                    setRowMenuPos(null);
                    setConfirm({ type: 'reset', entry: activeMenuEntry });
                  }}
                  role="menuitem"
                >
                  Reset password
                </button>
              )}
              {canChangeRoleEntry(activeMenuEntry) && (
                <button
                  type="button"
                  className="row-action-item"
                  onClick={() => {
                    setOpenMenuFor(0);
                    setRowMenuPos(null);
                    onOpenRoleModal(activeMenuEntry);
                  }}
                  role="menuitem"
                >
                  Change Role
                </button>
              )}
              {isCustomerMasterEntry(activeMenuEntry) && !isCurrentUserEntry(activeMenuEntry) && (
                <button
                  type="button"
                  className="row-action-item"
                  onClick={() => {
                    setOpenMenuFor(0);
                    setRowMenuPos(null);
                    setGeneralPasswordModal({ entry: activeMenuEntry });
                    setGeneralPasswordDraft({ password: '', confirmPassword: '' });
                  }}
                  role="menuitem"
                >
                  Change password
                </button>
              )}
              {!isCurrentUserEntry(activeMenuEntry) && !isCustomerMasterEntry(activeMenuEntry) && isHkAdmin && isSupportEntry(activeMenuEntry) && (
                <button
                  type="button"
                  className="row-action-item"
                  onClick={() => {
                    setOpenMenuFor(0);
                    setRowMenuPos(null);
                    const entryScopes = normalizeGroupScopes(activeMenuEntry).filter((s) => s.roleCode === 'SUPPORT');
                    const initialGroupIds = entryScopes.length > 0
                      ? entryScopes.map((s) => s.groupId).filter((id) => Number.isFinite(id) && id > 0)
                      : (activeMenuEntry.groupId ? [Number(activeMenuEntry.groupId)] : []);
                    setSupportGroupsModal({ entry: activeMenuEntry });
                    setSupportGroupsDraft(initialGroupIds);
                    setSupportGroupsPickerOpen(false);
                    setSupportGroupsPickerQuery('');
                  }}
                  role="menuitem"
                >
                  Manage groups
                </button>
              )}
              {!isCurrentUserEntry(activeMenuEntry) &&
                !isCustomerMasterEntry(activeMenuEntry) &&
                (isHkAdmin || isGroupScopeAdmin) &&
                isStandardUserEntry(activeMenuEntry) &&
                (!activeMenuEntry.groupId || !activeMenuEntry.storeId) && (
                <button
                  type="button"
                  className="row-action-item"
                  onClick={() => {
                    setOpenMenuFor(0);
                    setRowMenuPos(null);
                    setScopeModal({ entry: activeMenuEntry });
                    setScopeDraft({
                      groupId: activeMenuEntry.groupId ? String(activeMenuEntry.groupId) : '',
                      storeId: activeMenuEntry.storeId ? String(activeMenuEntry.storeId) : '',
                    });
                    setScopeStores(null);
                  }}
                  role="menuitem"
                >
                  Assign group/store
                </button>
              )}
              {!isCurrentUserEntry(activeMenuEntry) && !isCustomerMasterEntry(activeMenuEntry) && canManageStoresForEntry(activeMenuEntry) && (
                <button
                  type="button"
                  className="row-action-item"
                  onClick={() => {
                    setOpenMenuFor(0);
                    setRowMenuPos(null);
                    openStoresModal(activeMenuEntry);
                  }}
                  role="menuitem"
                >
                  Manage stores
                </button>
              )}
              {!isCurrentUserEntry(activeMenuEntry) && !activeMenuEntry.isActive && (
                <button
                  type="button"
                  className="row-action-item"
                  onClick={() => {
                    setOpenMenuFor(0);
                    setRowMenuPos(null);
                    setConfirm({ type: 'unblock', entry: activeMenuEntry });
                  }}
                  role="menuitem"
                >
                  Unblock user
                </button>
              )}
              {canDeleteEntry(activeMenuEntry) && (
                <button
                  type="button"
                  className="row-action-item danger"
                  onClick={() => {
                    setOpenMenuFor(0);
                    setRowMenuPos(null);
                    setConfirm({ type: 'delete', entry: activeMenuEntry });
                  }}
                  role="menuitem"
                >
                  {willRemoveScopeOnly(activeMenuEntry) ? 'Remove from group' : 'Delete user'}
                </button>
              )}
            </motion.div>
          )}

          {createOpen && (
            <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div
                className="modal-card"
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              >
              <h3>Add user</h3>
              <div className="profile-form">
                <RoundedSelect
                  value={createDraft.roleCode}
                  onChange={(val) => {
                    setError('');
                    setCreateAttempted(false);
                    setCreateDraft((current) => ({ ...current, roleCode: val }));
                  }}
                  disabled={isPureStoreAdmin || isPureMasterSalesStaff}
                  options={createRoleSelectOptions}
                />

                {String(createDraft.roleCode || '').toUpperCase() === 'CUSTOMER_MASTER' ? (
                  <>
                    <p className="hint-text">
                      Customer Master signs in by username/password via Kinde. The password you set is permanent (no forced change on first login). Scope is defined by the selected customer account.
                    </p>
                    <RoundedSelect
                      value={generalDraft.customerAccountId}
                      onChange={(value) => {
                        setGeneralDraft((current) => ({
                          ...current,
                          customerAccountId: value,
                          login: resolveCustomerMasterLoginFromAccount(value, generalCustomerAccounts),
                        }));
                      }}
                      searchable
                      searchPlaceholder="Search customer account..."
                      options={[
                        { value: '', label: 'Select customer account' },
                        ...generalCustomerAccounts.map((item) => ({
                          value: String(item.id),
                          label: item.name,
                        })),
                      ]}
                    />
                    <input
                      placeholder="Login (username)"
                      autoComplete="username"
                      value={generalDraft.login}
                      onChange={(event) => setGeneralDraft((current) => ({ ...current, login: event.target.value }))}
                    />
                    <input
                      placeholder="Email (optional)"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={generalDraft.email}
                      onChange={(event) => setGeneralDraft((current) => ({ ...current, email: event.target.value }))}
                    />
                    <input
                      placeholder="Password"
                      type="password"
                      autoComplete="new-password"
                      value={generalDraft.password}
                      onChange={(event) => setGeneralDraft((current) => ({ ...current, password: event.target.value }))}
                    />
                  </>
                ) : (
                  <>
                    <input
                      placeholder="First name *"
                      required
                      aria-required="true"
                      aria-invalid={createAttempted && !createDraft.firstName.trim()}
                      className={createAttempted && !createDraft.firstName.trim() ? 'input-invalid' : ''}
                      value={createDraft.firstName}
                      onChange={(event) => setCreateDraft((current) => ({ ...current, firstName: event.target.value }))}
                    />
                    <input
                      placeholder="Last name *"
                      required
                      aria-required="true"
                      aria-invalid={createAttempted && !createDraft.lastName.trim()}
                      className={createAttempted && !createDraft.lastName.trim() ? 'input-invalid' : ''}
                      value={createDraft.lastName}
                      onChange={(event) => setCreateDraft((current) => ({ ...current, lastName: event.target.value }))}
                    />
                    <div className="field-with-hint">
                      <input
                        placeholder="Email (e.g. name@company.com)"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        value={createDraft.email}
                        onChange={(event) => setCreateDraft((current) => ({ ...current, email: event.target.value }))}
                      />
                      <small className="field-hint">
                        Use a valid email address, e.g. <code>name@company.com</code>.
                      </small>
                    </div>
                    <input
                      placeholder="Phone"
                      value={createDraft.phoneNumber}
                      onChange={(event) => setCreateDraft((current) => ({ ...current, phoneNumber: event.target.value }))}
                    />
                    <input
                      placeholder="Password (optional)"
                      type="password"
                      autoComplete="new-password"
                      value={createDraft.password}
                      onChange={(event) => setCreateDraft((current) => ({ ...current, password: event.target.value }))}
                    />
                    <input
                      placeholder="Confirm password"
                      type="password"
                      autoComplete="new-password"
                      value={createDraft.confirmPassword}
                      onChange={(event) => setCreateDraft((current) => ({ ...current, confirmPassword: event.target.value }))}
                    />
                    <small className="field-hint">
                      If you set a password, it is permanent — the user can sign in with it and will not be forced to change it on first login.
                    </small>
                    {String(createDraft.roleCode || '').toUpperCase() !== 'HK_SUPPORT' && (
                      <>
                        {String(createDraft.roleCode || '').toUpperCase() === 'SUPPORT' ? (
                          <div className="multi-select-wrap open-up" ref={createSupportGroupsPickerRef}>
                            <button
                              type="button"
                              className="multi-select-trigger"
                              onClick={() => setCreateSupportGroupsPickerOpen((value) => !value)}
                              aria-expanded={createSupportGroupsPickerOpen}
                            >
                              <span className="multi-select-label">
                                {createSupportGroupIds.length === 0
                                  ? 'Select groups'
                                  : createSupportGroupIds.length === 1
                                    ? '1 group selected'
                                    : `${createSupportGroupIds.length} groups selected`}
                              </span>
                              <span className={createSupportGroupsPickerOpen ? 'rounded-select-caret open' : 'rounded-select-caret'} />
                            </button>
                            {createSupportGroupsPickerOpen && (
                              <div className="multi-select-menu">
                                <div className="multi-select-actions">
                                  <button
                                    type="button"
                                    className="ghost-button multi-select-action"
                                    onClick={() => setCreateSupportGroupIds(groups.map((g) => g.id))}
                                  >
                                    All
                                  </button>
                                  <button
                                    type="button"
                                    className="ghost-button multi-select-action"
                                    onClick={() => setCreateSupportGroupIds([])}
                                  >
                                    Clear
                                  </button>
                                </div>
                                <div className="multi-select-search">
                                  <input
                                    type="text"
                                    value={createSupportGroupsPickerQuery}
                                    onChange={(event) => setCreateSupportGroupsPickerQuery(event.target.value)}
                                    placeholder="Search groups..."
                                  />
                                </div>
                                <div className="multi-select-menu-list">
                                  {filteredCreateSupportGroups.map((g) => {
                                    const checked = createSupportGroupIds.includes(g.id);
                                    const fullName = String(g.name || '');
                                    return (
                                      <button
                                        key={g.id}
                                        type="button"
                                        className="multi-select-item"
                                        title={fullName}
                                        onClick={() =>
                                          setCreateSupportGroupIds((current) =>
                                            current.includes(g.id) ? current.filter((id) => id !== g.id) : [...current, g.id]
                                          )
                                        }
                                      >
                                        <span>
                                          {fullName.length > 35 ? `${fullName.slice(0, 35)}…` : fullName}
                                        </span>
                                        <span className={checked ? 'multi-check on' : 'multi-check'}>{checked ? '✓' : ''}</span>
                                      </button>
                                    );
                                  })}
                                  {filteredCreateSupportGroups.length === 0 && (
                                    <div className="multi-select-empty">No groups found.</div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <RoundedSelect
                            value={createDraft.groupId}
                            onChange={(val) => setCreateDraft((current) => ({ ...current, groupId: val, storeId: '' }))}
                            disabled={isPureStoreAdmin || isPureMasterSalesStaff || isGroupScopeAdmin}
                            searchable
                            searchPlaceholder="Search groups..."
                            menuMaxHeight={280}
                            maxLabelLength={35}
                            options={createGroupOptions}
                          />
                        )}
                        <RoundedSelect
                          value={createDraft.storeId}
                          onChange={(val) => setCreateDraft((current) => ({ ...current, storeId: val }))}
                          disabled={isCreateStoreLocked}
                          searchable
                          searchPlaceholder="Search stores..."
                          menuMaxHeight={280}
                          maxLabelLength={35}
                          options={createStoreOptions}
                        />
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="inline-row">
                <button type="button" className="ghost-button admin-inline-button" onClick={() => setCreateOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="primary-button admin-inline-button" disabled={isSaving} onClick={onCreateUser}>
                  Create
                </button>
              </div>
              </motion.div>
            </motion.div>
          )}

          {generalPasswordModal && (
            <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div
                className="modal-card"
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              >
                <h3>Change password</h3>
                <p className="hint-text">
                  Set a new password for general user <strong>{generalPasswordModal.entry?.email || generalPasswordModal.entry?.userId}</strong>.
                </p>
                <div className="profile-form">
                  <input
                    placeholder="New password"
                    type="password"
                    autoComplete="new-password"
                    value={generalPasswordDraft.password}
                    onChange={(event) => setGeneralPasswordDraft((current) => ({ ...current, password: event.target.value }))}
                  />
                  <input
                    placeholder="Confirm new password"
                    type="password"
                    autoComplete="new-password"
                    value={generalPasswordDraft.confirmPassword}
                    onChange={(event) => setGeneralPasswordDraft((current) => ({ ...current, confirmPassword: event.target.value }))}
                  />
                </div>
                <div className="inline-row">
                  <button type="button" className="ghost-button admin-inline-button" onClick={() => setGeneralPasswordModal(null)}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-button admin-inline-button"
                    disabled={isActionBusy}
                    onClick={async () => {
                      const entry = generalPasswordModal?.entry;
                      if (!entry) return;
                      const password = String(generalPasswordDraft.password || '');
                      const confirmPassword = String(generalPasswordDraft.confirmPassword || '');
                      if (!password || password.length < 8) {
                        setError('Password must be at least 8 characters.');
                        return;
                      }
                      if (password !== confirmPassword) {
                        setError('Password confirmation does not match.');
                        return;
                      }
                      const ok = await onChangeCustomerMasterPassword(entry, password);
                      if (ok) {
                        setGeneralPasswordModal(null);
                        setGeneralPasswordDraft({ password: '', confirmPassword: '' });
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {importOpen && (
            <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div
                className="modal-card import-modal"
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              >
                <h3>Import users</h3>
                <p className="hint-text">Upload an Excel (.xlsx) or CSV file. Use the template to get started.</p>
                <div className="inline-row" style={{ justifyContent: 'flex-start', marginBottom: '10px' }}>
                  <button type="button" className="ghost-button admin-inline-button compact" onClick={downloadExcelTemplate}>
                    <Download size={14} />
                    Download template
                  </button>
                </div>

                <input
                  id="usersCsvInput"
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="file-picker-hidden"
                  onChange={(event) => {
                    setImportResult(null);
                    setImportFile(event.target.files?.[0] || null);
                  }}
                />
                <div className="file-picker-row">
                  <button type="button" className="file-picker-button" onClick={() => document.getElementById('usersCsvInput')?.click()}>
                    Choose file
                  </button>
                  <span className="file-name">{importFile?.name || 'No file selected'}</span>
                </div>

                {importResult && (
                  <div className="status-banner success">
                    <strong>Import result</strong>
                    <span>Created: {importResult.created || 0}</span>
                    <span>Failed: {importResult.failed || 0}</span>
                    {Array.isArray(importResult.errors) && importResult.errors.length > 0 && (
                      <div className="import-errors-list">
                        {importResult.errors.slice(0, 8).map((err, idx) => (
                          <div key={`${err.row || idx}-${idx}`} className="hint-text">
                            Row {err.row || '?'}: {err.message || 'Failed'}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="inline-row">
                  <button type="button" className="ghost-button admin-inline-button" onClick={() => setImportOpen(false)} disabled={isImporting}>
                    Close
                  </button>
                  <button type="button" className="primary-button admin-inline-button" onClick={onImportCsv} disabled={!importFile || isImporting}>
                    {isImporting ? 'Importing...' : 'Import'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {roleModal && (
            <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div
                className="modal-card"
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              >
                <h3>Change Role for {roleModal.entry.email}</h3>
                <p className="hint-text">
                  Editing {roleModal.entry.roleLabel || roleModal.entry.roleCode}
                  {roleModal.entry.storeName
                    ? ` · ${roleModal.entry.storeName}`
                    : roleModal.entry.groupName
                      ? ` · ${roleModal.entry.groupName}`
                      : ''}
                  . Other roles for this user stay unchanged.
                </p>
                <div className="profile-form">
                  <RoundedSelect
                    value={roleDraft.roleCode}
                    onChange={(val) => setRoleDraft((current) => ({ ...current, roleCode: val }))}
                    options={changeRoleSelectOptions}
                  />
                  {roleRequiresGroup && (
                    <RoundedSelect
                      value={roleDraft.groupId}
                      onChange={(val) => setRoleDraft((current) => ({ ...current, groupId: val, storeId: '' }))}
                      searchable
                      searchPlaceholder="Search groups..."
                      menuMaxHeight={260}
                      placeholder="Select group"
                      options={[
                        { value: '', label: 'Select group' },
                        ...groups.map((group) => ({ value: String(group.id), label: group.name })),
                      ]}
                    />
                  )}
                  {roleRequiresStore && (
                    <>
                      <RoundedSelect
                        value={roleDraft.groupId}
                        onChange={(val) => setRoleDraft((current) => ({ ...current, groupId: val, storeId: '' }))}
                        searchable
                        searchPlaceholder="Search groups..."
                        menuMaxHeight={260}
                        placeholder="Select group (optional)"
                        options={[
                          { value: '', label: 'All groups' },
                          ...groups.map((group) => ({ value: String(group.id), label: group.name })),
                        ]}
                      />
                      <RoundedSelect
                        value={roleDraft.storeId}
                        onChange={(val) => setRoleDraft((current) => ({ ...current, storeId: val }))}
                        searchable
                        searchPlaceholder="Search stores..."
                        menuMaxHeight={260}
                        placeholder="Select store"
                        options={[
                          { value: '', label: 'Select store' },
                          ...(roleModalStores.length ? roleModalStores : stores).map((store) => ({
                            value: String(store.id),
                            label: store.name,
                          })),
                        ]}
                      />
                    </>
                  )}
                </div>
                <div className="inline-row">
                  <button type="button" className="ghost-button admin-inline-button" onClick={onCloseRoleModal} disabled={isRoleSaving}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-button admin-inline-button"
                    disabled={
                      isRoleSaving
                      || !roleDraft.roleCode
                      || (roleRequiresGroup && !roleDraft.groupId)
                      || (roleRequiresStore && !roleDraft.storeId)
                    }
                    onClick={onSaveRoleChange}
                  >
                    {isRoleSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {confirm && (
            <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div
                className="modal-card"
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              >
                <h3>
                  {confirm.type === 'delete'
                    ? willRemoveScopeOnly(confirm.entry)
                      ? 'Remove group access'
                      : 'Delete user'
                    : confirm.type === 'reset'
                      ? 'Reset password'
                      : confirm.type === 'block'
                        ? 'Block user'
                        : 'Unblock user'}
                </h3>
                <p className="hint-text">
                  {confirm.type === 'delete'
                    ? getDeleteConfirmMessage(confirm.entry)
                    : confirm.type === 'reset'
                      ? `This will request a password reset. The user will be prompted to change password on next sign-in. Continue?`
                      : confirm.type === 'block'
                        ? `This will block the user from signing in. Continue?`
                        : `This will unblock the user. Continue?`}
                </p>
                <div className="inline-row">
                  <button type="button" className="ghost-button admin-inline-button" onClick={() => setConfirm(null)} disabled={isActionBusy}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-button admin-inline-button"
                    disabled={isActionBusy || isSaving}
                    onClick={async () => {
                      const entry = confirm.entry;
                      const type = confirm.type;
                      setConfirm(null);
                      if (type === 'block' || type === 'unblock') {
                        await onToggleUserStatus(entry);
                        return;
                      }
                      if (type === 'reset') {
                        await onResetPassword(entry);
                        return;
                      }
                      if (type === 'delete') {
                        await onDeleteUser(entry);
                      }
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {supportGroupsModal && (
            <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div
                className="modal-card"
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              >
                <h3>Manage support groups</h3>
                <p className="hint-text">
                  Select groups for <strong>{supportGroupsModal.entry.email}</strong>.
                </p>
                <div className="profile-form">
                  <div className="multi-select-wrap" ref={supportGroupsPickerRef}>
                    <button
                      type="button"
                      className="multi-select-trigger"
                      onClick={() => setSupportGroupsPickerOpen((value) => !value)}
                      aria-expanded={supportGroupsPickerOpen}
                    >
                      <span className="multi-select-label">
                        {supportGroupsDraft.length === 0
                          ? 'Select groups'
                          : supportGroupsDraft.length === 1
                            ? '1 group selected'
                            : `${supportGroupsDraft.length} groups selected`}
                      </span>
                      <span className={supportGroupsPickerOpen ? 'rounded-select-caret open' : 'rounded-select-caret'} />
                    </button>
                    {supportGroupsPickerOpen && (
                      <div className="multi-select-menu">
                        <div className="multi-select-actions">
                          <button
                            type="button"
                            className="ghost-button multi-select-action"
                            onClick={() => setSupportGroupsDraft(groups.map((g) => g.id))}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            className="ghost-button multi-select-action"
                            onClick={() => setSupportGroupsDraft([])}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="multi-select-search">
                          <input
                            type="text"
                            value={supportGroupsPickerQuery}
                            onChange={(event) => setSupportGroupsPickerQuery(event.target.value)}
                            placeholder="Search groups..."
                          />
                        </div>
                        <div className="multi-select-menu-list">
                          {filteredSupportGroupsForModal.map((g) => {
                            const checked = supportGroupsDraft.includes(g.id);
                            return (
                              <button
                                key={g.id}
                                type="button"
                                className="multi-select-item"
                                onClick={() =>
                                  setSupportGroupsDraft((current) =>
                                    current.includes(g.id) ? current.filter((id) => id !== g.id) : [...current, g.id]
                                  )
                                }
                              >
                                <span>{g.name}</span>
                                <span className={checked ? 'multi-check on' : 'multi-check'}>{checked ? '✓' : ''}</span>
                              </button>
                            );
                          })}
                          {filteredSupportGroupsForModal.length === 0 && (
                            <div className="multi-select-empty">No groups found.</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="inline-row">
                  <button
                    type="button"
                    className="ghost-button admin-inline-button"
                    onClick={() => {
                      setSupportGroupsModal(null);
                      setSupportGroupsDraft([]);
                      setSupportGroupsPickerOpen(false);
                      setSupportGroupsPickerQuery('');
                    }}
                    disabled={isActionBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-button admin-inline-button"
                    disabled={isActionBusy}
                    onClick={async () => {
                      const entry = supportGroupsModal?.entry;
                      if (!entry) return;
                      if (supportGroupsDraft.length === 0) {
                        setError('Select at least one group.');
                        return;
                      }
                      setIsActionBusy(true);
                      setError('');
                      try {
                        const response = await fetch(`/api/admin/roles/users/${entry.userId}/support-groups`, {
                          method: 'PUT',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            groupIds: supportGroupsDraft.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0),
                          }),
                        });
                        if (!response.ok) {
                          const payload = await response.json().catch(() => null);
                          throw new Error(payload?.message || 'Failed to update support groups.');
                        }
                        setSuccess('Support groups updated.');
                        setSupportGroupsModal(null);
                        setSupportGroupsDraft([]);
                        const updated = await fetchAllUsersPayload();
                        applyUsersPayload(updated);
                      } catch (saveError) {
                        setError(saveError.message || 'Failed to update support groups.');
                      } finally {
                        setIsActionBusy(false);
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {scopeModal && (
            <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div
                className="modal-card"
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              >
                <h3>Assign group/store</h3>
                <p className="hint-text">
                  Set missing scope for <strong>{scopeModal.entry.email}</strong>.
                </p>
                <div className="profile-form">
                  <RoundedSelect
                    value={scopeDraft.groupId}
                    onChange={(val) => setScopeDraft((current) => ({ ...current, groupId: val }))}
                    searchable
                    searchPlaceholder="Search groups..."
                    menuMaxHeight={280}
                    options={[
                      { value: '', label: 'No group scope' },
                      ...groups.map((group) => ({ value: String(group.id), label: group.name })),
                    ]}
                  />
                  <RoundedSelect
                    value={scopeDraft.storeId}
                    onChange={(val) => setScopeDraft((current) => ({ ...current, storeId: val }))}
                    searchable
                    searchPlaceholder="Search stores..."
                    menuMaxHeight={280}
                    options={[
                      { value: '', label: 'No store scope' },
                      ...storesForScopeGroup.map((store) => ({ value: String(store.id), label: store.name })),
                    ]}
                  />
                </div>

                <div className="inline-row">
                  <button
                    type="button"
                    className="ghost-button admin-inline-button"
                    onClick={() => {
                      setScopeModal(null);
                      setScopeDraft({ groupId: '', storeId: '' });
                    }}
                    disabled={isActionBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="primary-button admin-inline-button"
                    onClick={onAssignMissingScope}
                    disabled={isActionBusy}
                  >
                    Save
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
          {storesModal && (
            <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <motion.div
                className="modal-card"
                initial={{ opacity: 0, y: 30, scale: 0.94 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 18, scale: 0.98 }}
                transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
              >
                <h3>Manage stores</h3>
                <p className="hint-text">
                  Select one or multiple stores for <strong>{storesModal.entry.email}</strong>.
                </p>
                <div className="search-input-wrap">
                  <Search size={14} />
                  <input
                    value={storesModalSearch}
                    onChange={(event) => setStoresModalSearch(event.target.value)}
                    placeholder="Search stores..."
                  />
                </div>
                <div className="modal-role-list manage-stores-list">
                  {filteredStoresForManageModal.map((store) => {
                    const checked = selectedStoreIds.includes(store.id);
                    return (
                      <button
                        key={store.id}
                        type="button"
                        className={checked ? 'modal-role-option selected' : 'modal-role-option'}
                        onClick={() =>
                          setSelectedStoreIds((current) =>
                            current.includes(store.id) ? current.filter((id) => id !== store.id) : [...current, store.id]
                          )
                        }
                      >
                        <span>{store.name}</span>
                        <span className="hint-text">{checked ? 'Selected' : 'Not selected'}</span>
                      </button>
                    );
                  })}
                  {filteredStoresForManageModal.length === 0 && <p className="hint-text">No stores found.</p>}
                </div>
                <div className="inline-row">
                  <button type="button" className="ghost-button admin-inline-button" onClick={() => setStoresModal(null)} disabled={isActionBusy}>
                    Cancel
                  </button>
                  <button type="button" className="primary-button admin-inline-button" onClick={onSaveUserStores} disabled={isActionBusy}>
                    Save
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
          ,
          document.body
        )}

      <FloatingNotices
        error={error}
        success={success}
        onDismissError={() => setError('')}
        onDismissSuccess={() => setSuccess('')}
      />
    </section>
  );
}
